import { loadDB, saveDB, newId, type AnalysisDB, type Observation, type Rating, type Setup } from "./store";
import { toggleMode, themeIcon, currentMode } from "../core/appTheme";

const LOGO = `<svg class="brand-logo" viewBox="0 0 32 32" width="34" height="34"><rect width="32" height="32" rx="8"/><path d="M7 21l5-7 4 3 9-10" stroke-width="2.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const RATINGS: { id: Rating; label: string }[] = [
  { id: "good", label: "Good" },
  { id: "watch", label: "Watch" },
  { id: "avoid", label: "Avoid" },
  { id: "none", label: "—" },
];

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
}
function fmt(n: number): string {
  const r = Math.round(n * 100) / 100;
  return r.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

export function showAnalysis() {
  const app = document.getElementById("app")!;
  let db: AnalysisDB = loadDB();
  let activeSetup = "all"; // "all" or a setup id
  let focusMetric = db.metrics[0]?.id ?? "";
  let addingSetup = false;

  const save = () => saveDB(db);

  app.innerHTML = `
    <div class="an-wrap">
      <div class="home-top">
        <div class="auth-brand">${LOGO}<div><div class="auth-title">Analysis</div><div class="auth-sub">Filter research workbench</div></div></div>
        <div style="display:flex;gap:8px;align-items:center">
          <button class="mini-btn icon-btn" id="an-theme" title="Toggle light / dark">${themeIcon(currentMode())}</button>
          <a class="mini-btn" href="/">← Home</a>
        </div>
      </div>
      <div class="an-setupbar" id="an-setupbar"></div>
      <div class="an-body">
        <div class="an-datacol">
          <div class="an-setuphead" id="an-setuphead"></div>
          <div class="an-tablewrap"><table class="an-table" id="an-table"></table></div>
          <div class="an-actions">
            <button class="mini-btn primary" id="an-add-obs">+ Add scenario</button>
            <div id="an-metric-add"></div>
          </div>
        </div>
        <div class="an-vizcol" id="an-viz"></div>
      </div>
    </div>`;

  const themeBtn = app.querySelector<HTMLElement>("#an-theme")!;
  themeBtn.addEventListener("click", () => {
    toggleMode();
    themeBtn.innerHTML = themeIcon(currentMode());
  });

  // ---- helpers to query the current view ----
  const visibleObs = (): Observation[] =>
    db.observations
      .filter((o) => activeSetup === "all" || o.setupId === activeSetup)
      .sort((a, b) => a.createdAt - b.createdAt);
  const setupOf = (id: string) => db.setups.find((s) => s.id === id);

  // ---- setup chips ----
  function renderSetupBar() {
    const bar = app.querySelector("#an-setupbar")!;
    const chip = (id: string, label: string) =>
      `<button class="an-chip ${activeSetup === id ? "active" : ""}" data-setup="${id}">${label}</button>`;
    const addPart = addingSetup
      ? `<span class="an-inline-form">
           <input id="an-setup-code" class="an-inline-input code" placeholder="CODE" maxlength="8" />
           <input id="an-setup-desc" class="an-inline-input" placeholder="description (optional)" />
           <button class="mini-btn primary" id="an-setup-save">Add</button>
           <button class="an-icon" id="an-setup-cancel" title="Cancel">✕</button>
         </span>`
      : `<button class="an-chip add" id="an-add-setup">+ Setup</button>`;
    bar.innerHTML = chip("all", "All") + db.setups.map((s) => chip(s.id, esc(s.code))).join("") + addPart;

    bar.querySelectorAll<HTMLElement>("[data-setup]").forEach((b) =>
      b.addEventListener("click", () => {
        activeSetup = b.dataset.setup!;
        addingSetup = false;
        renderAll();
      })
    );

    if (addingSetup) {
      const code = bar.querySelector<HTMLInputElement>("#an-setup-code")!;
      const desc = bar.querySelector<HTMLInputElement>("#an-setup-desc")!;
      code.focus();
      const cancel = () => {
        addingSetup = false;
        renderSetupBar();
      };
      const commit = () => {
        const c = code.value.trim().toUpperCase();
        if (!c) {
          cancel();
          return;
        }
        const s: Setup = { id: newId("s"), code: c, desc: desc.value.trim() };
        db.setups.push(s);
        activeSetup = s.id;
        addingSetup = false;
        save();
        renderAll();
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Enter") commit();
        else if (e.key === "Escape") cancel();
      };
      code.addEventListener("keydown", onKey);
      desc.addEventListener("keydown", onKey);
      bar.querySelector("#an-setup-save")!.addEventListener("click", commit);
      bar.querySelector("#an-setup-cancel")!.addEventListener("click", cancel);
    } else {
      bar.querySelector("#an-add-setup")!.addEventListener("click", () => {
        addingSetup = true;
        renderSetupBar();
      });
    }
  }

  // ---- inline "add filter" form ----
  function renderMetricAdd() {
    const slot = app.querySelector<HTMLElement>("#an-metric-add")!;
    slot.innerHTML = `<button class="mini-btn" id="an-add-metric">+ Add filter</button>`;
    slot.querySelector("#an-add-metric")!.addEventListener("click", () => {
      slot.innerHTML = `<span class="an-inline-form">
          <input id="an-metric-name" class="an-inline-input wide" placeholder="filter name (e.g. body_%_avg)" />
          <button class="mini-btn primary" id="an-metric-save">Add</button>
          <button class="an-icon" id="an-metric-cancel" title="Cancel">✕</button>
        </span>`;
      const inp = slot.querySelector<HTMLInputElement>("#an-metric-name")!;
      inp.focus();
      const commit = () => {
        const label = inp.value.trim();
        if (!label) {
          renderMetricAdd();
          return;
        }
        const id = newId("m");
        db.metrics.push({ id, label });
        focusMetric = id;
        save();
        renderAll();
        renderMetricAdd();
      };
      inp.addEventListener("keydown", (e) => {
        if (e.key === "Enter") commit();
        else if (e.key === "Escape") renderMetricAdd();
      });
      slot.querySelector("#an-metric-save")!.addEventListener("click", commit);
      slot.querySelector("#an-metric-cancel")!.addEventListener("click", () => renderMetricAdd());
    });
  }

  // ---- setup header (rename/delete when a single setup is active) ----
  function renderSetupHead() {
    const head = app.querySelector<HTMLElement>("#an-setuphead")!;
    if (activeSetup === "all") {
      head.innerHTML = `<div class="an-setuphead-inner"><b>All setups</b><span class="an-muted">${db.observations.length} scenarios · ${db.metrics.length} filters</span></div>`;
      return;
    }
    const s = setupOf(activeSetup);
    if (!s) return;
    head.innerHTML = `
      <div class="an-setuphead-inner">
        <span class="an-code">${esc(s.code)}</span>
        <input class="an-desc-input" id="an-desc" value="${esc(s.desc)}" placeholder="description" />
        <button class="mini-btn danger" id="an-del-setup">Delete setup</button>
      </div>`;
    const desc = head.querySelector<HTMLInputElement>("#an-desc")!;
    desc.addEventListener("input", () => {
      s.desc = desc.value;
      save();
    });
    head.querySelector("#an-del-setup")!.addEventListener("click", () => {
      const n = db.observations.filter((o) => o.setupId === s.id).length;
      if (!confirm(`Delete setup "${s.code}" and its ${n} scenario(s)?`)) return;
      db.observations = db.observations.filter((o) => o.setupId !== s.id);
      db.setups = db.setups.filter((x) => x.id !== s.id);
      activeSetup = "all";
      save();
      renderAll();
    });
  }

  // ---- data table ----
  function renderTable() {
    const table = app.querySelector<HTMLTableElement>("#an-table")!;
    const metricCols = db.metrics
      .map(
        (m) =>
          `<th class="an-mcol" draggable="true" data-mid="${m.id}"><span class="an-grip" title="Drag to reorder">⠿</span><span class="an-mlabel" title="${esc(
            m.label
          )}">${esc(m.label)}</span>
            <span class="an-mhbtns"><button class="an-icon" data-mren="${m.id}" title="Rename">✎</button><button class="an-icon" data-mdel="${m.id}" title="Delete filter">✕</button></span></th>`
      )
      .join("");
    const head = `<thead><tr>
      <th>Setup</th>
      ${metricCols}
      <th>Rating</th>
      <th>Notes</th>
      <th></th>
    </tr></thead>`;

    const obs = visibleObs();
    const rows =
      obs.length === 0
        ? `<tr><td colspan="99" class="an-empty">No scenarios yet. Add one to start logging filter values.</td></tr>`
        : obs
            .map((o) => {
              const setupSel = `<select class="an-setupsel" data-setupsel="${o.id}">${db.setups
                .map((s) => `<option value="${s.id}" ${s.id === o.setupId ? "selected" : ""}>${esc(s.code)}</option>`)
                .join("")}</select>`;
              const cells = db.metrics
                .map(
                  (m) =>
                    `<td><input class="an-val" data-obs="${o.id}" data-metric="${m.id}" inputmode="decimal" value="${
                      o.values[m.id] == null ? "" : o.values[m.id]
                    }" placeholder="—" /></td>`
                )
                .join("");
              const ratingSel = `<select class="an-rating an-r-${o.rating}" data-rate="${o.id}">${RATINGS.map(
                (r) => `<option value="${r.id}" ${o.rating === r.id ? "selected" : ""}>${r.label}</option>`
              ).join("")}</select>`;
              return `<tr>
                <td>${setupSel}</td>
                ${cells}
                <td>${ratingSel}</td>
                <td><input class="an-notes" data-notes="${o.id}" value="${esc(o.notes)}" placeholder="notes…" /></td>
                <td><button class="an-icon del" data-delobs="${o.id}" title="Delete">✕</button></td>
              </tr>`;
            })
            .join("");
    table.innerHTML = head + `<tbody>${rows}</tbody>`;

    // value edits
    table.querySelectorAll<HTMLInputElement>(".an-val").forEach((inp) =>
      inp.addEventListener("input", () => {
        const o = db.observations.find((x) => x.id === inp.dataset.obs)!;
        const raw = inp.value.trim();
        const v = raw === "" ? null : parseFloat(raw);
        o.values[inp.dataset.metric!] = raw === "" ? null : isFinite(v as number) ? v : null;
        save();
        renderViz();
      })
    );
    table.querySelectorAll<HTMLInputElement>(".an-name").forEach((inp) =>
      inp.addEventListener("input", () => {
        db.observations.find((x) => x.id === inp.dataset.name)!.name = inp.value;
        save();
        renderViz();
      })
    );
    table.querySelectorAll<HTMLInputElement>(".an-notes").forEach((inp) =>
      inp.addEventListener("input", () => {
        db.observations.find((x) => x.id === inp.dataset.notes)!.notes = inp.value;
        save();
      })
    );
    table.querySelectorAll<HTMLSelectElement>(".an-rating").forEach((sel) =>
      sel.addEventListener("change", () => {
        const o = db.observations.find((x) => x.id === sel.dataset.rate)!;
        o.rating = sel.value as Rating;
        save();
        renderTable();
        renderViz();
      })
    );
    table.querySelectorAll<HTMLSelectElement>(".an-setupsel").forEach((sel) =>
      sel.addEventListener("change", () => {
        db.observations.find((x) => x.id === sel.dataset.setupsel)!.setupId = sel.value;
        save();
        renderTable();
        renderViz();
      })
    );
    table.querySelectorAll<HTMLElement>("[data-delobs]").forEach((b) =>
      b.addEventListener("click", () => {
        db.observations = db.observations.filter((x) => x.id !== b.dataset.delobs);
        save();
        renderTable();
        renderViz();
      })
    );
    table.querySelectorAll<HTMLElement>("[data-mren]").forEach((b) =>
      b.addEventListener("click", () => {
        const m = db.metrics.find((x) => x.id === b.dataset.mren)!;
        const n = prompt("Rename filter", m.label);
        if (n && n.trim()) {
          m.label = n.trim();
          save();
          renderAll();
        }
      })
    );
    table.querySelectorAll<HTMLElement>("[data-mdel]").forEach((b) =>
      b.addEventListener("click", () => {
        const m = db.metrics.find((x) => x.id === b.dataset.mdel)!;
        if (!confirm(`Delete filter "${m.label}"? Its values will be removed from all scenarios.`)) return;
        db.metrics = db.metrics.filter((x) => x.id !== m.id);
        db.observations.forEach((o) => delete o.values[m.id]);
        if (focusMetric === m.id) focusMetric = db.metrics[0]?.id ?? "";
        save();
        renderAll();
      })
    );

    // ---- drag column headers to reorder filters ----
    const reorderMetric = (fromId: string, toId: string, after: boolean) => {
      const arr = db.metrics;
      const fromIdx = arr.findIndex((m) => m.id === fromId);
      if (fromIdx < 0 || fromId === toId) return;
      const [item] = arr.splice(fromIdx, 1);
      const toIdx = arr.findIndex((m) => m.id === toId);
      if (toIdx < 0) arr.splice(fromIdx, 0, item);
      else arr.splice(after ? toIdx + 1 : toIdx, 0, item);
      save();
      renderAll();
    };
    let dragId: string | null = null;
    const cols = table.querySelectorAll<HTMLElement>(".an-mcol[data-mid]");
    const clearDrop = () => cols.forEach((c) => c.classList.remove("drop-before", "drop-after"));
    cols.forEach((th) => {
      th.addEventListener("dragstart", (e) => {
        dragId = th.dataset.mid!;
        (e as DragEvent).dataTransfer!.effectAllowed = "move";
        th.classList.add("dragging");
      });
      th.addEventListener("dragend", () => {
        th.classList.remove("dragging");
        clearDrop();
        dragId = null;
      });
      th.addEventListener("dragover", (e) => {
        e.preventDefault();
        if (!dragId || th.dataset.mid === dragId) return;
        (e as DragEvent).dataTransfer!.dropEffect = "move";
        const r = th.getBoundingClientRect();
        const after = (e as DragEvent).clientX > r.left + r.width / 2;
        clearDrop();
        th.classList.add(after ? "drop-after" : "drop-before");
      });
      th.addEventListener("dragleave", () => th.classList.remove("drop-before", "drop-after"));
      th.addEventListener("drop", (e) => {
        e.preventDefault();
        const toId = th.dataset.mid!;
        if (!dragId || dragId === toId) return;
        const r = th.getBoundingClientRect();
        const after = (e as DragEvent).clientX > r.left + r.width / 2;
        reorderMetric(dragId, toId, after);
      });
    });
  }

  // ---- visualization / analysis ----
  function renderViz() {
    const col = app.querySelector<HTMLElement>("#an-viz")!;
    if (!db.metrics.length) {
      col.innerHTML = `<div class="an-viz-empty">Add a filter to see analysis.</div>`;
      return;
    }
    if (!db.metrics.some((m) => m.id === focusMetric)) focusMetric = db.metrics[0].id;
    const metric = db.metrics.find((m) => m.id === focusMetric)!;
    const obs = visibleObs().filter((o) => typeof o.values[focusMetric] === "number");
    const vals = obs.map((o) => o.values[focusMetric] as number);
    const maxAbs = Math.max(1, ...vals.map((v) => Math.abs(v)));
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : NaN;
    const min = vals.length ? Math.min(...vals) : NaN;
    const max = vals.length ? Math.max(...vals) : NaN;

    const pills = db.metrics
      .map(
        (m) => `<button class="an-mpill ${m.id === focusMetric ? "active" : ""}" data-focus="${m.id}">${esc(m.label)}</button>`
      )
      .join("");

    const bars = obs.length
      ? obs
          .map((o) => {
            const v = o.values[focusMetric] as number;
            const su = setupOf(o.setupId);
            const w = (Math.abs(v) / maxAbs) * 100;
            return `<div class="an-bar-row">
              <div class="an-bar-label" title="${esc(su?.code ?? "")} · ${esc(o.name)}"><span class="an-badge sm">${esc(
              su?.code ?? "?"
            )}</span> ${esc(o.name || "—")}</div>
              <div class="an-bar-track"><div class="an-bar an-r-${o.rating}" style="width:${w}%"></div><span class="an-bar-val">${fmt(
              v
            )}</span></div>
            </div>`;
          })
          .join("")
      : `<div class="an-viz-empty">No values logged for <b>${esc(metric.label)}</b> in this view yet.</div>`;

    const avgMark = vals.length
      ? `<div class="an-avgline" style="left:calc(${(Math.abs(avg) / maxAbs) * 100}% )"><span>avg ${fmt(avg)}</span></div>`
      : "";

    col.innerHTML = `
      <div class="an-viz-head">Analysis</div>
      <div class="an-mpills">${pills}</div>
      <div class="an-stats">
        <div class="an-stat"><span>Count</span><b>${vals.length}</b></div>
        <div class="an-stat"><span>Avg</span><b>${vals.length ? fmt(avg) : "—"}</b></div>
        <div class="an-stat"><span>Min</span><b>${vals.length ? fmt(min) : "—"}</b></div>
        <div class="an-stat"><span>Max</span><b>${vals.length ? fmt(max) : "—"}</b></div>
      </div>
      <div class="an-chart">${bars}<div class="an-chart-overlay">${avgMark}</div></div>`;

    col.querySelectorAll<HTMLElement>("[data-focus]").forEach((b) =>
      b.addEventListener("click", () => {
        focusMetric = b.dataset.focus!;
        renderViz();
      })
    );
  }

  function renderAll() {
    renderSetupBar();
    renderSetupHead();
    renderTable();
    renderViz();
  }
  renderMetricAdd();

  // ---- global actions ----
  app.querySelector("#an-add-obs")!.addEventListener("click", () => {
    const setupId = activeSetup !== "all" ? activeSetup : db.setups[0]?.id;
    if (!setupId) {
      alert("Add a setup first.");
      return;
    }
    const o: Observation = {
      id: newId("o"),
      setupId,
      name: "",
      rating: "none",
      values: {},
      notes: "",
      createdAt: Date.now(),
    };
    db.observations.push(o);
    save();
    renderTable();
    renderViz();
    // focus the new row's first value cell
    const inp = app.querySelector<HTMLInputElement>(`.an-val[data-obs="${o.id}"]`);
    inp?.focus();
  });

  renderAll();
}
